import React, { useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { ChevronRight } from 'lucide-react';
import { useOrderHistory } from '../../../../stores/appOrderHistoryStore';
import { usePriceAlertsStore } from '../../../../stores/appPriceAlertsStore';
import { useRestaurantStore } from '../../../../stores/appRestaurantStore';
import { useTimeIntervalStore } from '../../../../stores/appTimeIntervalStore';
import { useLoadOrderHistory, useLoadPriceAlerts } from '../../../../utils/dataLoadingUtils';
import { SupplierLogo as SharedSupplierLogo } from '../../../ui/SupplierLogo';
import { eur } from '../../../../components/orderhistory/utils';
import { cn } from '../../../../lib/utils';
import { tertiaryText, smallTextSize, mediumText } from '../../../../styling/stylingHelper';
import { TableComponent } from '../../../ui/TableComponent';
import { ColumnFilterDropdown } from '../../../ui/ColumnFilterDropdown';
import { rangeFromTimeControls, previousRange } from '../../../../utils/reports/shared';
import { cachedReportValue } from '../../../../utils/reports/reportMemoCache';
import {
  SectionCard,
  EmptyCard,
  Kpi,
  KpiGrid,
  ChangeBadge,
  ReportLoading,
  ReportTable,
  ReportColumn,
  ReportBreadcrumb,
  ReportSelect,
  PillToggle,
  pctAbs,
} from './common';
import {
  computeSupplierReport,
  computeSupplierDetail,
  computeCategoryReport,
  computeCategoryDetail,
  SupplierOverviewRow,
  SupplierProductRow,
  CategoryOverviewRow,
  CategoryProductRow,
} from '../../../../utils/reports/supplierReport';

type ViewMode = 'supplier' | 'category';

const SupplierLogo: React.FC<{ row: { platform: SupplierOverviewRow['platform']; unknownSupplierName?: string; label: string } }> = ({
  row,
}) => (
  <SharedSupplierLogo platform={row.platform} unknownSupplierName={row.unknownSupplierName} alt={row.label} />
);

const SupplierReport: React.FC = () => {
  const { t } = useTranslation();

  // Data is scoped to the selected restaurant by these hooks.
  useLoadOrderHistory();
  useLoadPriceAlerts();

  const orders = useOrderHistory((s) => s.orderHistoryList);
  const isLoadingOrders = useOrderHistory((s) => s.isLoading);
  const priceAlerts = usePriceAlertsStore((s) => s.priceAlerts);
  const restaurants = useRestaurantStore((s) => s.restaurants);
  const restaurantNames = useMemo(
    () => Object.fromEntries(restaurants.map((r) => [r.id, r.name])),
    [restaurants]
  );

  const view = useTimeIntervalStore((s) => s.view);
  const startDate = useTimeIntervalStore((s) => s.startDate);
  const endDate = useTimeIntervalStore((s) => s.endDate);

  // Top-level split: by supplier (default) or by category across suppliers.
  const [mode, setMode] = useState<ViewMode>('supplier');
  // Supplier mode: selected supplier + its in-detail category filter.
  const [selectedKey, setSelectedKey] = useState<string | null>(null);
  const [categoryFilters, setCategoryFilters] = useState<string[]>([]);
  // Category mode: selected category + its in-detail supplier filter.
  const [selectedCategory, setSelectedCategory] = useState<string | null>(null);
  const [supplierFilter, setSupplierFilter] = useState<string>('all');

  const overview = cachedReportValue('supplier.overview', [orders, view, startDate, endDate], () => {
    const range = rangeFromTimeControls(view, startDate, endDate);
    const previous = previousRange(range);
    return computeSupplierReport({ orders, range, previous });
  });

  const categoryOverview = cachedReportValue('supplier.categoryOverview', [orders, view, startDate, endDate], () => {
    const range = rangeFromTimeControls(view, startDate, endDate);
    const previous = previousRange(range);
    return computeCategoryReport({ orders, range, previous });
  });

  const detail = useMemo(() => {
    if (!selectedKey) return null;
    const range = rangeFromTimeControls(view, startDate, endDate);
    return computeSupplierDetail({ orders, priceAlerts, range, supplierKey: selectedKey, categoryFilters });
  }, [orders, priceAlerts, selectedKey, categoryFilters, view, startDate, endDate]);

  const categoryDetail = useMemo(() => {
    if (selectedCategory === null) return null;
    const range = rangeFromTimeControls(view, startDate, endDate);
    return computeCategoryDetail({ orders, priceAlerts, range, category: selectedCategory, supplierFilter });
  }, [orders, priceAlerts, selectedCategory, supplierFilter, view, startDate, endDate]);

  const selectSupplier = (key: string) => {
    setSelectedKey(key);
    setCategoryFilters([]);
  };
  const selectCategory = (category: string) => {
    setSelectedCategory(category);
    setSupplierFilter('all');
  };
  const switchMode = (next: ViewMode) => {
    setMode(next);
    setSelectedKey(null);
    setSelectedCategory(null);
  };

  // While orders are still loading and empty, show a spinner instead of flashing
  // the "no orders" empty state.
  if (isLoadingOrders && orders.length === 0) {
    return <ReportLoading />;
  }

  if (overview.suppliers.length === 0) {
    return <EmptyCard message={t('reports.supplier.empty')} />;
  }

  const categoryLabel = (label: string) => label || t('reports.supplier.category.uncategorised');

  const modeToggle = (
    <PillToggle<ViewMode>
      options={[
        { value: 'supplier', label: t('reports.supplier.viewMode.bySupplier') },
        { value: 'category', label: t('reports.supplier.viewMode.byCategory') },
      ]}
      value={mode}
      onChange={switchMode}
    />
  );

  // --- Category mode ------------------------------------------------------
  if (mode === 'category') {
    // Category detail (a category was picked and still has products this period).
    if (selectedCategory !== null && categoryDetail) {
      return (
        <div className="mt-4">
          <ReportBreadcrumb
            items={[
              { label: t('reports.supplier.breadcrumb.categories'), onClick: () => setSelectedCategory(null) },
              { label: categoryLabel(categoryDetail.label) },
            ]}
          />

          <h2 className="text-xl font-bold text-textPrimary dark:text-textPrimary-dark mb-5">
            {categoryLabel(categoryDetail.label)}
          </h2>

          {/* Header KPIs */}
          <KpiGrid className="lg:grid-cols-3">
            <Kpi label={t('reports.supplier.kpi.spend')} value={eur(categoryDetail.spend)} />
            <Kpi label={t('reports.supplier.kpi.shareOfPurchasing')} value={pctAbs(categoryDetail.percentOfTotal)} />
            <Kpi label={t('reports.supplier.kpi.products')} value={String(categoryDetail.productCount)} />
            <Kpi label={t('reports.supplier.kpi.suppliers')} value={String(categoryDetail.supplierCount)} />
            <Kpi label={t('reports.supplier.kpi.priceIncreases')} value={String(categoryDetail.priceIncreaseCount)} />
            <Kpi
              label={t('reports.supplier.kpi.priceImpact')}
              value={eur(categoryDetail.priceChangeImpactEur)}
              tone={categoryDetail.priceChangeImpactEur > 0 ? 'up' : categoryDetail.priceChangeImpactEur < 0 ? 'down' : 'neutral'}
            />
          </KpiGrid>

          {/* Supplier filter — drill the category down to a single supplier. */}
          {categoryDetail.supplierOptions.length > 0 && (
            <div className="flex flex-wrap items-center gap-2 mb-4">
              <span className={cn(tertiaryText, smallTextSize, mediumText)}>
                {t('reports.supplier.category.filter.supplier')}
              </span>
              <ReportSelect value={supplierFilter} onChange={(e) => setSupplierFilter(e.target.value)}>
                <option value="all">{t('reports.supplier.category.filter.allSuppliers')}</option>
                {categoryDetail.supplierOptions.map((s) => (
                  <option key={s.value} value={s.value}>
                    {s.label || t('reports.supplier.unknownSupplier')}
                  </option>
                ))}
              </ReportSelect>
            </div>
          )}

          {/* Product table (across suppliers, with a supplier column). */}
          <CategoryProductTable rows={categoryDetail.products} unknownSupplierLabel={t('reports.supplier.unknownSupplier')} t={t} />

          <p className="text-xs text-muted dark:text-muted-dark">{t('reports.supplier.priceImpactFootnote')}</p>
        </div>
      );
    }

    // Category overview / picker.
    const categoryColumns: ReportColumn<CategoryOverviewRow>[] = [
      {
        key: 'category',
        header: t('reports.supplier.categoryOverview.category'),
        render: (row) => <span className="font-medium">{categoryLabel(row.label)}</span>,
      },
      { key: 'spend', header: t('reports.supplier.categoryOverview.spend'), align: 'right', render: (row) => <span className="font-medium">{eur(row.spend)}</span> },
      { key: 'share', header: t('reports.supplier.categoryOverview.share'), align: 'right', render: (row) => pctAbs(row.percentOfTotal) },
      { key: 'products', header: t('reports.supplier.categoryOverview.products'), align: 'right', render: (row) => row.productCount },
      { key: 'suppliers', header: t('reports.supplier.categoryOverview.suppliers'), align: 'right', render: (row) => row.supplierCount },
      { key: 'trend', header: t('reports.supplier.categoryOverview.trend'), align: 'right', render: (row) => <ChangeBadge value={row.spendChangePercent} goodWhenUp={false} hidden={categoryOverview.comparisonHidden} /> },
      { key: 'chevron', header: '', align: 'right', render: () => <ChevronRight className="w-4 h-4 inline text-muted dark:text-muted-dark" /> },
    ];

    return (
      <div className="mt-4">
        <div className="mb-4">{modeToggle}</div>
        <SectionCard title={t('reports.supplier.categoryOverview.title')} subtitle={t('reports.supplier.categoryOverview.subtitle')}>
          <ReportTable
            columns={categoryColumns}
            rows={categoryOverview.categories}
            rowKey={(row) => row.category || '__uncategorised__'}
            onRowClick={(row) => selectCategory(row.category)}
          />
        </SectionCard>
      </div>
    );
  }

  // --- Supplier mode ------------------------------------------------------
  // Detail view (a supplier was picked and still has orders this period).
  if (selectedKey && detail) {
    return (
      <div className="mt-4">
        <ReportBreadcrumb
          items={[
            { label: t('reports.supplier.breadcrumb.suppliers'), onClick: () => setSelectedKey(null) },
            { label: detail.label || t('reports.supplier.unknownSupplier') },
          ]}
        />

        <div className="flex items-center gap-3 mb-5">
          <SupplierLogo row={detail} />
          <h2 className="text-xl font-bold text-textPrimary dark:text-textPrimary-dark">
            {detail.label || t('reports.supplier.unknownSupplier')}
          </h2>
        </div>

        {/* Header KPIs */}
        <KpiGrid className="lg:grid-cols-3">
          <Kpi label={t('reports.supplier.kpi.spend')} value={eur(detail.spend)} />
          <Kpi label={t('reports.supplier.kpi.shareOfPurchasing')} value={pctAbs(detail.percentOfTotal)} />
          <Kpi label={t('reports.supplier.kpi.orders')} value={String(detail.orderCount)} />
          <Kpi label={t('reports.supplier.kpi.avgOrder')} value={eur(detail.orderCount > 0 ? detail.spend / detail.orderCount : 0)} />
          <Kpi label={t('reports.supplier.kpi.priceIncreases')} value={String(detail.priceIncreaseCount)} />
          <Kpi label={t('reports.supplier.kpi.priceImpact')} value={eur(detail.priceChangeImpactEur)} tone={detail.priceChangeImpactEur > 0 ? 'up' : detail.priceChangeImpactEur < 0 ? 'down' : 'neutral'} />
        </KpiGrid>

        {/* Category filter — standalone "easy access" control, kept in sync with
            the in-column Category header filter on the product table below
            (both bound to the same categoryFilters state). */}
        {detail.categoryOptions.length > 0 && (
          <div className="flex flex-wrap items-center gap-2 mb-4">
            <span className={cn(tertiaryText, smallTextSize, mediumText)}>
              {t('reports.supplier.filter.category')}
            </span>
            <ColumnFilterDropdown
              selectedValues={categoryFilters}
              onValuesChange={setCategoryFilters}
              availableOptions={detail.categoryOptions.map((c) => c.value)}
              valueType="category"
              filterLabel={t('reports.supplier.filter.allCategories')}
              align="left"
            />
          </div>
        )}

        {/* Per-restaurant spend split (holding view; only when >1 restaurant) */}
        {detail.spendByRestaurant.length > 1 && (
          <SectionCard
            title={t('reports.supplier.byRestaurant.title')}
            subtitle={t('reports.supplier.byRestaurant.subtitle')}
          >
            <ReportTable
              columns={[
                { key: 'restaurant', header: t('reports.supplier.byRestaurant.restaurant'), render: (r) => <span className="font-medium">{restaurantNames[r.restaurantId] || r.restaurantId}</span> },
                { key: 'orders', header: t('reports.supplier.byRestaurant.orders'), align: 'right', render: (r) => r.orderCount },
                { key: 'spend', header: t('reports.supplier.byRestaurant.spend'), align: 'right', render: (r) => <span className="font-medium">{eur(r.spend)}</span> },
                { key: 'share', header: t('reports.supplier.byRestaurant.share'), align: 'right', render: (r) => pctAbs(detail.spend > 0 ? (r.spend / detail.spend) * 100 : 0) },
              ] as ReportColumn<{ restaurantId: string; spend: number; orderCount: number }>[]}
              rows={detail.spendByRestaurant}
              rowKey={(r) => r.restaurantId}
            />
          </SectionCard>
        )}

        {/* Product table — reuses the order-history TableComponent so columns
            match (blue kg/L quantity label, price-change pill), with sortable
            columns and an in-column category filter synced to the control above. */}
        <ProductTable
          rows={detail.products}
          categoryFilters={categoryFilters}
          setCategoryFilters={setCategoryFilters}
          categoryOptionValues={detail.categoryOptions.map((c) => c.value)}
          t={t}
        />

        <p className="text-xs text-muted dark:text-muted-dark">{t('reports.supplier.priceImpactFootnote')}</p>
      </div>
    );
  }

  // Overview / picker.
  const overviewColumns: ReportColumn<SupplierOverviewRow>[] = [
    {
      key: 'supplier',
      header: t('reports.supplier.overview.supplier'),
      render: (row) => (
        <div className="flex items-center gap-2">
          <SupplierLogo row={row} />
          <span className="font-medium">{row.label || t('reports.supplier.unknownSupplier')}</span>
        </div>
      ),
    },
    { key: 'spend', header: t('reports.supplier.overview.spend'), align: 'right', render: (row) => <span className="font-medium">{eur(row.spend)}</span> },
    { key: 'share', header: t('reports.supplier.overview.shareOfPurchasing'), align: 'right', render: (row) => pctAbs(row.percentOfTotal) },
    { key: 'orders', header: t('reports.supplier.overview.orders'), align: 'right', render: (row) => row.orderCount },
    { key: 'avgOrder', header: t('reports.supplier.overview.avgOrder'), align: 'right', render: (row) => eur(row.avgOrderValue) },
    { key: 'trend', header: t('reports.supplier.overview.trend'), align: 'right', render: (row) => <ChangeBadge value={row.spendChangePercent} goodWhenUp={false} hidden={overview.comparisonHidden} /> },
    { key: 'chevron', header: '', align: 'right', render: () => <ChevronRight className="w-4 h-4 inline text-muted dark:text-muted-dark" /> },
  ];
  return (
    <div className="mt-4">
      <div className="mb-4">{modeToggle}</div>
      <SectionCard title={t('reports.supplier.overview.title')} subtitle={t('reports.supplier.overview.subtitle')}>
        <ReportTable
          columns={overviewColumns}
          rows={overview.suppliers}
          rowKey={(row) => row.supplierKey}
          onRowClick={(row) => selectSupplier(row.supplierKey)}
        />
      </SectionCard>
    </div>
  );
};

// --- product tables -------------------------------------------------------

/** Numeric sort shared by the supplier + category product tables: rows missing the
 *  sorted value sink to the bottom regardless of direction. */
function useSortedRows<T>(rows: T[], sortColumn: string | null, sortDirection: 'asc' | 'desc' | null, getSortValue: (row: T) => number | undefined) {
  return useMemo(() => {
    if (!sortColumn || !sortDirection) return rows;
    const dir = sortDirection === 'asc' ? 1 : -1;
    return [...rows].sort((a, b) => {
      const av = getSortValue(a);
      const bv = getSortValue(b);
      const aMissing = av === undefined || Number.isNaN(av);
      const bMissing = bv === undefined || Number.isNaN(bv);
      if (aMissing && bMissing) return 0;
      if (aMissing) return 1;
      if (bMissing) return -1;
      return (av! - bv!) * dir;
    });
    // getSortValue is stable per call site (defined inline); deps mirror the inputs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [rows, sortColumn, sortDirection]);
}

const ProductTable: React.FC<{
  rows: SupplierProductRow[];
  categoryFilters: string[];
  setCategoryFilters: (values: string[]) => void;
  categoryOptionValues: string[];
  t: (k: string) => string;
}> = ({ rows, categoryFilters, setCategoryFilters, categoryOptionValues, t }) => {
  // Sorting is controlled here (same pattern as OrderHistoryProductsPage): we
  // hold the sort state and sort the rows before handing them to TableComponent.
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedRows = useSortedRows(rows, sortColumn, sortDirection, (r) => {
    switch (sortColumn) {
      case 'orders': return r.orderCount;
      case 'firstPrice': return r.firstPrice;
      case 'latestPrice': return r.latestPrice;
      case 'priceChange': return r.priceChangePercent;
      case 'spend': return r.spend;
      case 'share': return r.shareOfSupplierSpend;
      default: return undefined;
    }
  });

  return (
    <TableComponent
      title={t('reports.supplier.products.title')}
      data={sortedRows}
      blankLines={false}
      usePagination={true}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      columns={[
        { key: 'name', label: t('reports.supplier.products.product'), columnType: 'name' },
        {
          key: 'category',
          label: t('reports.supplier.products.category'),
          columnType: 'category',
          filter: {
            selectedValues: categoryFilters,
            onValuesChange: setCategoryFilters,
            availableOptions: categoryOptionValues,
            valueType: 'category',
            filterLabel: t('reports.supplier.filter.allCategories'),
          },
        },
        { key: 'formattedQuantity', label: t('reports.supplier.products.quantity'), columnType: 'blueQuantity' },
        { key: 'orderCount', label: t('reports.supplier.products.orders'), columnType: 'base', sortable: true, sortKey: 'orders' },
        { key: 'firstPrice', label: t('reports.supplier.products.firstPrice'), columnType: 'currency', sortable: true, sortKey: 'firstPrice' },
        { key: 'latestPrice', label: t('reports.supplier.products.latestPrice'), columnType: 'boldPrice', sortable: true, sortKey: 'latestPrice' },
        { key: 'priceChangePercent', label: t('reports.supplier.products.priceChange'), columnType: 'priceAlert', sortable: true, sortKey: 'priceChange' },
        {
          key: 'unitPriceDisplay',
          label: t('reports.supplier.products.unitPrice'),
          render: (value: SupplierProductRow['unitPriceDisplay']) => (
            <span className={cn(tertiaryText, smallTextSize)}>{value ?? '—'}</span>
          ),
        },
        { key: 'spend', label: t('reports.supplier.products.spend'), columnType: 'currencyHighlightBlack', sortable: true, sortKey: 'spend' },
        {
          key: 'shareOfSupplierSpend',
          label: t('reports.supplier.products.share'),
          render: (value: number) => <>{pctAbs(value)}</>,
          sortable: true,
          sortKey: 'share',
        },
      ]}
    />
  );
};

/** Category product table: same TableComponent look as the supplier product table,
 *  but with a supplier column (a category spans suppliers) and "share of category
 *  spend" in the share column. */
const CategoryProductTable: React.FC<{
  rows: CategoryProductRow[];
  unknownSupplierLabel: string;
  t: (k: string) => string;
}> = ({ rows, unknownSupplierLabel, t }) => {
  const [sortColumn, setSortColumn] = useState<string | null>(null);
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc' | null>(null);

  const handleSort = (column: string) => {
    if (sortColumn === column) {
      setSortDirection(sortDirection === 'asc' ? 'desc' : 'asc');
    } else {
      setSortColumn(column);
      setSortDirection('asc');
    }
  };

  const sortedRows = useSortedRows(rows, sortColumn, sortDirection, (r) => {
    switch (sortColumn) {
      case 'orders': return r.orderCount;
      case 'firstPrice': return r.firstPrice;
      case 'latestPrice': return r.latestPrice;
      case 'priceChange': return r.priceChangePercent;
      case 'spend': return r.spend;
      case 'share': return r.shareOfSupplierSpend;
      default: return undefined;
    }
  });

  return (
    <TableComponent
      title={t('reports.supplier.category.products.title')}
      data={sortedRows}
      blankLines={false}
      usePagination={true}
      sortColumn={sortColumn}
      sortDirection={sortDirection}
      onSort={handleSort}
      columns={[
        {
          key: 'supplierLabel',
          label: t('reports.supplier.overview.supplier'),
          render: (_value: string, row: CategoryProductRow) => (
            <div className="flex items-center gap-2">
              <SupplierLogo row={{ platform: row.platform, unknownSupplierName: row.unknownSupplierName, label: row.supplierLabel }} />
              <span>{row.supplierLabel || unknownSupplierLabel}</span>
            </div>
          ),
        },
        { key: 'name', label: t('reports.supplier.products.product'), columnType: 'name' },
        { key: 'formattedQuantity', label: t('reports.supplier.products.quantity'), columnType: 'blueQuantity' },
        { key: 'orderCount', label: t('reports.supplier.products.orders'), columnType: 'base', sortable: true, sortKey: 'orders' },
        { key: 'firstPrice', label: t('reports.supplier.products.firstPrice'), columnType: 'currency', sortable: true, sortKey: 'firstPrice' },
        { key: 'latestPrice', label: t('reports.supplier.products.latestPrice'), columnType: 'boldPrice', sortable: true, sortKey: 'latestPrice' },
        { key: 'priceChangePercent', label: t('reports.supplier.products.priceChange'), columnType: 'priceAlert', sortable: true, sortKey: 'priceChange' },
        {
          key: 'unitPriceDisplay',
          label: t('reports.supplier.products.unitPrice'),
          render: (value: CategoryProductRow['unitPriceDisplay']) => (
            <span className={cn(tertiaryText, smallTextSize)}>{value ?? '—'}</span>
          ),
        },
        { key: 'spend', label: t('reports.supplier.products.spend'), columnType: 'currencyHighlightBlack', sortable: true, sortKey: 'spend' },
        {
          key: 'shareOfSupplierSpend',
          label: t('reports.supplier.products.share'),
          render: (value: number) => <>{pctAbs(value)}</>,
          sortable: true,
          sortKey: 'share',
        },
      ]}
    />
  );
};

export default SupplierReport;
